import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { PresentationSession, PresentationParticipant, SessionAnalytics } from '@/types'
import { generateId, generateUserFriendlyId } from '@/lib/utils'

interface PresentationState {
  // Current session state
  currentSession: PresentationSession | null
  isPresenterMode: boolean
  isParticipantMode: boolean
  participantId: string | null
  
  // Session management
  activeSessions: PresentationSession[]
  
  // Real-time data
  liveParticipants: PresentationParticipant[]
  sessionAnalytics: SessionAnalytics | null
  
  // Actions
  createSession: (title: string, settings?: Partial<PresentationSession['settings']>) => string
  joinSession: (sessionId: string, participantName?: string) => boolean
  leaveSession: () => void
  updateParticipantProgress: (participantId: string, currentStage: number) => void
  setPresenterMode: (sessionId: string) => void
  updateSessionSettings: (sessionId: string, settings: Partial<PresentationSession['settings']>) => void
  advanceAllToStage: (stageId: number) => void
  endSession: (sessionId: string) => void
  
  // Analytics
  calculateSessionAnalytics: () => SessionAnalytics
  getStageCompletionRates: () => Record<number, number>
  getAverageTimePerStage: () => Record<number, number>
}

export const usePresentationStore = create<PresentationState>()(
  persist(
    (set, get) => ({
      currentSession: null,
      isPresenterMode: false,
      isParticipantMode: false,
      participantId: null,
      activeSessions: [],
      liveParticipants: [],
      sessionAnalytics: null,

      createSession: (title, settings = {}) => {
        const sessionId = generateId()
        const presenterId = generateId()
        
        const newSession: PresentationSession = {
          id: sessionId,
          title,
          presenterId,
          audienceSize: 0,
          currentStage: 1,
          isLive: true,
          settings: {
            allowIndividualProgress: true,
            showAggregateProgress: true,
            enableAudienceChat: false,
            timePerStage: undefined,
            ...settings
          },
          participants: [],
          analytics: {
            totalParticipants: 0,
            activeParticipants: 0,
            avgTimePerStage: {},
            stageCompletionRates: {},
            mostCommonProblems: [],
            engagementScore: 0
          }
        }

        set(state => ({
          activeSessions: [...state.activeSessions, newSession],
          currentSession: newSession,
          isPresenterMode: true,
          isParticipantMode: false
        }))

        return sessionId
      },

      joinSession: (sessionId, participantName) => {
        const { activeSessions } = get()
        const session = activeSessions.find(s => s.id === sessionId)
        
        if (!session || !session.isLive) {
          return false
        }

        const participantId = generateUserFriendlyId('User')
        const participant: PresentationParticipant = {
          id: participantId,
          name: participantName || participantId,
          currentStage: session.currentStage,
          joinedAt: new Date(),
          isActive: true,
          progress: {
            userId: participantId,
            currentStage: session.currentStage,
            stageProgress: {}
          }
        }

        // Update session with new participant
        const updatedSession = {
          ...session,
          participants: [...session.participants, participant],
          audienceSize: session.audienceSize + 1,
          analytics: {
            ...session.analytics,
            totalParticipants: session.analytics.totalParticipants + 1,
            activeParticipants: session.analytics.activeParticipants + 1
          }
        }

        set(state => ({
          activeSessions: state.activeSessions.map(s => 
            s.id === sessionId ? updatedSession : s
          ),
          currentSession: updatedSession,
          isParticipantMode: true,
          isPresenterMode: false,
          participantId,
          liveParticipants: [...state.liveParticipants, participant]
        }))

        return true
      },

      leaveSession: () => {
        const { currentSession, participantId } = get()
        
        if (currentSession && participantId) {
          const updatedParticipants = currentSession.participants.map(p =>
            p.id === participantId ? { ...p, isActive: false } : p
          )

          const updatedSession = {
            ...currentSession,
            participants: updatedParticipants,
            analytics: {
              ...currentSession.analytics,
              activeParticipants: Math.max(0, currentSession.analytics.activeParticipants - 1)
            }
          }

          set(state => ({
            activeSessions: state.activeSessions.map(s =>
              s.id === currentSession.id ? updatedSession : s
            ),
            currentSession: null,
            isParticipantMode: false,
            participantId: null,
            liveParticipants: state.liveParticipants.filter(p => p.id !== participantId)
          }))
        } else {
          // Just clear local state if presenter or no session
          set({
            currentSession: null,
            isPresenterMode: false,
            isParticipantMode: false,
            participantId: null
          })
        }
      },

      updateParticipantProgress: (participantId, currentStage) => {
        const { currentSession } = get()
        if (!currentSession) return

        const updatedParticipants = currentSession.participants.map(p =>
          p.id === participantId 
            ? { ...p, currentStage, progress: { ...p.progress, currentStage } }
            : p
        )

        const updatedSession = {
          ...currentSession,
          participants: updatedParticipants
        }

        set(state => ({
          currentSession: updatedSession,
          activeSessions: state.activeSessions.map(s =>
            s.id === currentSession.id ? updatedSession : s
          ),
          liveParticipants: state.liveParticipants.map(p =>
            p.id === participantId ? { ...p, currentStage } : p
          )
        }))
      },

      setPresenterMode: (sessionId) => {
        const { activeSessions } = get()
        const session = activeSessions.find(s => s.id === sessionId)
        
        if (session) {
          set({
            currentSession: session,
            isPresenterMode: true,
            isParticipantMode: false,
            participantId: null
          })
        }
      },

      updateSessionSettings: (sessionId, settings) => {
        set(state => ({
          activeSessions: state.activeSessions.map(s =>
            s.id === sessionId 
              ? { ...s, settings: { ...s.settings, ...settings } }
              : s
          ),
          currentSession: state.currentSession?.id === sessionId
            ? { ...state.currentSession, settings: { ...state.currentSession.settings, ...settings } }
            : state.currentSession
        }))
      },

      advanceAllToStage: (stageId) => {
        const { currentSession } = get()
        if (!currentSession || !get().isPresenterMode) return

        const updatedParticipants = currentSession.participants.map(p => ({
          ...p,
          currentStage: stageId,
          progress: { ...p.progress, currentStage: stageId }
        }))

        const updatedSession = {
          ...currentSession,
          currentStage: stageId,
          participants: updatedParticipants
        }

        set(state => ({
          currentSession: updatedSession,
          activeSessions: state.activeSessions.map(s =>
            s.id === currentSession.id ? updatedSession : s
          )
        }))
      },

      endSession: (sessionId) => {
        set(state => ({
          activeSessions: state.activeSessions.map(s =>
            s.id === sessionId ? { ...s, isLive: false } : s
          ),
          currentSession: state.currentSession?.id === sessionId ? null : state.currentSession,
          isPresenterMode: false,
          isParticipantMode: false,
          participantId: null,
          liveParticipants: []
        }))
      },

      calculateSessionAnalytics: () => {
        const { currentSession } = get()
        if (!currentSession) {
          return {
            totalParticipants: 0,
            activeParticipants: 0,
            avgTimePerStage: {},
            stageCompletionRates: {},
            mostCommonProblems: [],
            engagementScore: 0
          }
        }

        const activeParticipants = currentSession.participants.filter(p => p.isActive)
        const stageCompletionRates: Record<number, number> = {}
        
        // Calculate completion rates for each stage
        for (let stage = 1; stage <= 5; stage++) {
          const completedCount = activeParticipants.filter(p => p.currentStage > stage).length
          stageCompletionRates[stage] = activeParticipants.length > 0 
            ? Math.round((completedCount / activeParticipants.length) * 100)
            : 0
        }

        // Calculate average time per stage (placeholder)
        const avgTimePerStage: Record<number, number> = {}
        for (let stage = 1; stage <= 5; stage++) {
          avgTimePerStage[stage] = 0 // Would be calculated from actual progress data
        }

        // Calculate engagement score based on progress and participation
        const avgProgress = activeParticipants.length > 0
          ? activeParticipants.reduce((sum, p) => sum + p.currentStage, 0) / activeParticipants.length
          : 0
        const engagementScore = Math.round((avgProgress / 5) * 100)

        const analytics: SessionAnalytics = {
          totalParticipants: currentSession.participants.length,
          activeParticipants: activeParticipants.length,
          avgTimePerStage,
          stageCompletionRates,
          mostCommonProblems: [], // Would be populated from user data
          engagementScore
        }

        set({ sessionAnalytics: analytics })
        return analytics
      },

      getStageCompletionRates: () => {
        const analytics = get().calculateSessionAnalytics()
        return analytics.stageCompletionRates
      },

      getAverageTimePerStage: () => {
        const analytics = get().calculateSessionAnalytics()
        return analytics.avgTimePerStage
      }
    }),
    {
      name: 'presentation-store',
      version: 1
    }
  )
)